<template>
  <div class="students-projects-page" :dir="lang === 'Ar' ? 'rtl' : 'ltr'">
    <Loading v-show="show" />

    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <h1>{{ translate.Projects.Title }}</h1>
        <p>{{ translate.Projects.Subtitle }}</p>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-container">
      <div class="filters">
        <div class="search-box">
          <i class="fa fa-search search-icon"></i>
          <input type="text" v-model="searchQuery" :placeholder="translate.Projects.SearchPlaceholder">
        </div>

        <div class="select-box">
          <select v-model="selectedMajor">
            <option v-for="major in majors" :key="major" :value="major">
              {{ major === 'كل التخصصات' ? translate.Projects.AllMajors : major }}
            </option>
          </select>
          <i class="fa fa-chevron-down arrow-icon"></i>
        </div>

        <div class="select-box">
          <select v-model="selectedYear">
            <option v-for="year in years" :key="year" :value="year">
              {{ year === 'كل السنوات' ? translate.Projects.AllYears : year }}
            </option>
          </select>
          <i class="fa fa-chevron-down arrow-icon"></i>
        </div>
      </div>
    </div>

    <!-- Projects Grid -->
    <div class="projects-container pb-5">
      <div class="projects-grid" v-if="filteredProjects.length > 0">
        <div class="project-card" v-for="project in filteredProjects" :key="project.postID">
          <div class="card-img-wrapper">
            <img :src="getImageUrl(project.postImage)" :alt="project.postTitle" @error="handleImageError" />
            <div class="year-badge">{{ getValue(project, 'txtYear') }}</div>
          </div>
          
          <div class="card-content">
            <h3 class="project-title">{{ project.postTitle }}</h3>
            
            <div class="project-meta">
              <span class="meta-item" :title="translate.Projects.Student">
                <i class="fa fa-user"></i>
                {{ getValue(project, 'txtstudentName') }}
              </span>
              <span class="meta-item" :title="translate.Projects.Major">
                <i class="fa fa-graduation-cap"></i>
                {{ getValue(project, 'txtUniversityMajor') }}
              </span>
            </div>

            <p class="project-desc">{{ project.postSubTitle }}</p>
            
            <router-link :to="{ name: 'StudentProjectDetails', params: { id: project.postID } }" class="details-btn">
              {{ translate.Projects.ViewDetails }}
              <i class="fa" :class="lang === 'Ar' ? 'fa-arrow-left' : 'fa-arrow-right'"></i>
            </router-link>
          </div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div class="empty-state" v-else-if="!show">
        <div class="empty-icon">🔍</div>
        <h3>{{ translate.Projects.NoProjectsFound }}</h3>
        <p>{{ translate.Projects.NotFoundDesc }}</p>
        <button class="reset-btn" @click="resetFilters">{{ translate.Projects.ResetSearch }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from './Loading.vue';
import axios from 'axios';
import ArJson from "../../public/i18n/Ar.json";
import EnJson from "../../public/i18n/En.json";

export default {
  name: 'StudentsProjects',
  components: {
    Loading
  },
  data() {
    return {
      projects: [],
      searchQuery: "",
      selectedMajor: "كل التخصصات",
      selectedYear: "كل السنوات",
      show: true,
      lang: "Ar",
      translate: ArJson[0],
      defaultImage: "https://via.placeholder.com/400x200?text=لا+توجد+صورة"
    };
  },
  computed: {
    majors() {
      const allMajors = this.projects
        .map(p => this.getValue(p, 'txtUniversityMajor'))
        .filter(m => m && m !== 'غير محدد');
      return [this.translate.Projects.AllMajors, ...new Set(allMajors)];
    },
    years() {
      const allYears = this.projects
        .map(p => this.getValue(p, 'txtYear'))
        .filter(y => y && y !== 'غير محدد');
      return [this.translate.Projects.AllYears, ...new Set(allYears)];
    },
    filteredProjects() {
      return this.projects.filter(p => {
        const major = this.getValue(p, 'txtUniversityMajor');
        const year = this.getValue(p, 'txtYear');
        const title = p.postTitle || "";
        const desc = p.postSubTitle || "";
        
        const matchSearch = title.toLowerCase().includes(this.searchQuery.toLowerCase()) || desc.toLowerCase().includes(this.searchQuery.toLowerCase());
        const matchMajor = this.selectedMajor === this.translate.Projects.AllMajors || major === this.selectedMajor;
        const matchYear = this.selectedYear === this.translate.Projects.AllYears || year === this.selectedYear;
        
        return matchSearch && matchMajor && matchYear;
      });
    }
  },
  mounted() {
    window.scroll(0, 0);
    this.lang = localStorage.getItem("lang") || "Ar";
    this.translate = this.lang === "Ar" ? ArJson[0] : EnJson[0];
    
    this.selectedMajor = this.translate.Projects.AllMajors;
    this.selectedYear = this.translate.Projects.AllYears;
    
    if (this.lang !== "Ar") {
      let link1 = document.createElement("link");
      link1.setAttribute("rel", "stylesheet");
      link1.id = "langfilewebsites";
      link1.setAttribute("href", "../../../style-ltr.css");
      if (!document.getElementById("langfilewebsites")) {
        document.head.appendChild(link1);
      }
    } else {
      document.getElementById("langfilewebsites")?.remove();
    }
    
    this.fetchProjects();
  },
  methods: {
    fetchProjects() {
      this.show = true;
      const bodyFormData = new FormData();
      bodyFormData.append("check", "getPost");
      bodyFormData.append("Lang", this.lang);
      bodyFormData.append("Type", "Univ.StudentsProjects");
      bodyFormData.append("ParentId", "NTROOT0");
      bodyFormData.append("Pno", "-1");

      axios({
        method: "post",
        url: "https://api2.yg-u.net:444/our_team/getPost",
        data: bodyFormData,
      })
      .then((response) => {
        if(Array.isArray(response.data)) {
          this.projects = response.data;
        }
        this.show = false;
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        this.show = false;
      });
    },
    getValue(project, key) {
      if (project[key]) return project[key];
      if (project[key + '1']) return project[key + '1'];
      return 'غير محدد';
    },
    getImageUrl(imageName) {
      if (!imageName) return this.defaultImage;
      return `https://api2.yg-u.net:444/images/post/${imageName}`;
    },
    handleImageError(e) {
      e.target.src = this.defaultImage;
    },
    resetFilters() {
      this.searchQuery = "";
      this.selectedMajor = this.translate.Projects.AllMajors;
      this.selectedYear = this.translate.Projects.AllYears;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');

.students-projects-page {
  font-family: 'Tajawal', sans-serif;
  background-color: #f4f7fa;
  min-height: 100vh;
}

/* Header Section */
.page-header {
  background: linear-gradient(135deg, #21467d 0%, #3498db 100%);
  padding: 60px 20px;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: url('https://www.transparenttextures.com/patterns/cubes.png');
  opacity: 0.1;
}

.header-content {
  position: relative;
  z-index: 1;
}

.header-content h1 {
  font-size: 42px;
  font-weight: 800;
  margin-bottom: 15px;
  color: #fff;
  text-align: center !important;
}

.header-content p {
  font-size: 18px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

/* Filters Section */
.filters-container {
  max-width: 1200px;
  margin: -35px auto 40px;
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

.filters {
  background: white;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
}

.search-box, .select-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box input, .select-box select {
  width: 100%;
  padding: 14px 45px 14px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  font-family: 'Tajawal', sans-serif;
  color: #334155;
  background-color: #f8fafc;
  transition: all 0.3s ease;
  appearance: none;
}

.search-box input:focus, .select-box select:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

[dir="rtl"] .search-icon { right: 15px; }
[dir="ltr"] .search-icon { left: 15px; }

[dir="ltr"] .search-box input { padding: 14px 20px 14px 45px; }

.arrow-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

[dir="rtl"] .arrow-icon { left: 15px; }
[dir="ltr"] .arrow-icon { right: 15px; }

/* Projects Grid */
.projects-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  width: 100%;
  box-sizing: border-box;
}

/* Project Card */
.project-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0; /* Important for grid items */
}

.project-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 30px rgba(0, 0, 0, 0.1);
}

.card-img-wrapper {
  position: relative;
  height: 220px;
  overflow: hidden;
}

.card-img-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.project-card:hover .card-img-wrapper img {
  transform: scale(1.05);
}

.year-badge {
  position: absolute;
  top: 15px;
  background: rgba(255, 255, 255, 0.9);
  color: #1e3c72;
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 14px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

[dir="rtl"] .year-badge { right: 15px; }
[dir="ltr"] .year-badge { left: 15px; }

.card-content {
  padding: 25px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.project-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.project-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

.meta-item {
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  padding: 5px 10px;
  border-radius: 8px;
}

.meta-item i {
  color: #3b82f6;
}

.project-desc {
  font-size: 15px;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 25px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

.details-btn {
  width: 100%;
  padding: 14px;
  border: none;
  background: #f8fafc;
  color: #1e3c72;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  font-family: 'Tajawal', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.project-card:hover .details-btn {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
}

.details-btn i {
  transition: transform 0.3s ease;
}

[dir="rtl"] .project-card:hover .details-btn i {
  transform: translateX(-5px);
}

[dir="ltr"] .project-card:hover .details-btn i {
  transform: translateX(5px);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 20px;
  margin: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  font-size: 60px;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #1e293b;
  font-size: 24px;
  margin-bottom: 10px;
}

.empty-state p {
  color: #64748b;
  margin-bottom: 25px;
}

.reset-btn {
  padding: 12px 25px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Tajawal', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.reset-btn:hover {
  background: #2563eb;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 40px 15px;
  }

  .header-content h1 {
    font-size: 28px;
  }

  .header-content p {
    font-size: 15px;
  }
  
  .filters-container {
    margin-top: -25px;
  }

  .filters {
    flex-direction: column;
    padding: 15px;
    gap: 10px;
  }
  
  .search-box, .select-box {
    width: 100%;
    min-width: unset;
  }

  .search-box input, .select-box select {
    padding-top: 12px;
    padding-bottom: 12px;
    font-size: 14px;
  }
  
  .projects-grid {
    grid-template-columns: 1fr;
    padding: 0;
    gap: 20px;
    width: 100%;
  }

  .projects-container {
    padding: 0 15px;
  }

  .project-card {
    border-radius: 15px;
  }

  .card-img-wrapper {
    height: 180px;
  }

  .card-content {
    padding: 15px;
  }

  .project-title {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .project-meta {
    gap: 8px;
    margin-bottom: 10px;
  }

  .meta-item {
    font-size: 12px;
    padding: 4px 8px;
  }

  .project-desc {
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 20px;
    -webkit-line-clamp: 2;
  }

  .details-btn {
    padding: 12px;
    font-size: 14px;
  }
}
</style>
